import { dropUndefinedKeys, generateSentryTraceHeader, timestampInSeconds } from '@sentry/utils'; // These are aligned with OpenTelemetry trace flags

var TRACE_FLAG_NONE = 0x0;
var TRACE_FLAG_SAMPLED = 0x1;
/**
 * Convert a span to a trace context, which can be sent as the `trace` context in an event.
 */

function spanToTraceContext(span) {
  var _span$spanContext = span.spanContext(),
      span_id = _span$spanContext.spanId,
      trace_id = _span$spanContext.traceId;

  var _spanToJSON = spanToJSON(span),
      data = _spanToJSON.data,
      op = _spanToJSON.op,
      parent_span_id = _spanToJSON.parent_span_id,
      status = _spanToJSON.status,
      tags = _spanToJSON.tags,
      origin = _spanToJSON.origin;

  return dropUndefinedKeys({
    data: data,
    op: op,
    parent_span_id: parent_span_id,
    span_id: span_id,
    status: status,
    tags: tags,
    trace_id: trace_id,
    origin: origin
  });
}
/**
 * Convert a Span to a Sentry trace header.
 */


function spanToTraceHeader(span) {
  var _span$spanContext2 = span.spanContext(),
      traceId = _span$spanContext2.traceId,
      spanId = _span$spanContext2.spanId;

  var sampled = spanIsSampled(span);
  return generateSentryTraceHeader(traceId, spanId, sampled);
}
/**
 * Convert a span time input intp a timestamp in seconds.
 */


function spanTimeInputToSeconds(input) {
  if (typeof input === 'number') {
    return ensureTimestampInSeconds(input);
  }

  if (Array.isArray(input)) {
    // See {@link HrTime} for the array-based time format
    return input[0] + input[1] / 1e9;
  }

  if (input instanceof Date) {
    return ensureTimestampInSeconds(input.getTime());
  }

  return timestampInSeconds();
}
/**
 * Converts a timestamp to second, if it was in milliseconds, or keeps it as second.
 */


function ensureTimestampInSeconds(timestamp) {
  var isMs = timestamp > 9999999999;
  return isMs ? timestamp / 1000 : timestamp;
}
/**
 * Convert a span to a JSON representation.
 * Note that all fields returned here are optional and need to be guarded against.
 *
 * Note: Because of this, we currently have a circular type dependency (which we opted out of in package.json).
 * This is not avoidable as we need `spanToJSON` in `spanUtils.ts`, which in turn is needed by `span.ts` for backwards compatibility.
 * And `spanToJSON` needs the Span class from `span.ts` to check here.
 * TODO v8: When we remove the deprecated stuff from `span.ts`, we can remove the circular dependency again.
 */


function spanToJSON(span) {
  if (spanIsSpanClass(span)) {
    return span.getSpanJSON();
  } // Fallback: We also check for `.toJSON()` here...
  // eslint-disable-next-line deprecation/deprecation


  if (typeof span.toJSON === 'function') {
    // eslint-disable-next-line deprecation/deprecation
    return span.toJSON();
  }

  return {};
}
/**
 * Sadly, due to circular dependency checks we cannot actually import the Span class here and check for instanceof.
 * :( So instead we approximate this by checking if it has the `getSpanJSON` method.
 */


function spanIsSpanClass(span) {
  return typeof span.getSpanJSON === 'function';
}
/**
 * Returns true if a span is sampled.
 * In most cases, you should just use `span.isRecording()` instead.
 * However, this has a slightly different semantic, as it also returns false if the span is finished.
 * So in the case where this distinction is important, use this method.
 */


function spanIsSampled(span) {
  // We align our trace flags with the ones OpenTelemetry use
  // So we also check for sampled the same way they do.
  var _span$spanContext3 = span.spanContext(),
      traceFlags = _span$spanContext3.traceFlags; // eslint-disable-next-line no-bitwise


  return Boolean(traceFlags & TRACE_FLAG_SAMPLED);
}

export { TRACE_FLAG_NONE, TRACE_FLAG_SAMPLED, spanIsSampled, spanTimeInputToSeconds, spanToJSON, spanToTraceContext, spanToTraceHeader };